import * as React from 'react'
import { fromJSX } from 'widgets-for-react'
import { getClassNameModern } from '../../../../../../Shared/Frontend/utils/getClassNameFromStyle'
import { slugify } from '../../../../../../Shared/Frontend/utils/slugify'
import { DP } from '../../../drupal/blockDefinitions'
import { ctaSrc, ctaSrcSet } from '../../../drupal/utils/imageSizes'
import { parseDrupalImageUrls } from '../../../drupal/utils/modernImage'
import { dummyData, randomTitle, randomWords } from '../../../utils/dummyData'
import { linkWrapper } from '../../../utils/link'
import { ModernBlock, SeoEvent } from '../../modernBlocks.state'
import { ctaBlockClassesDefault, ctaBlockClassesNew, CtaWithBgColourBlockLayout } from './ctaWithBgColourBlock.layout'
import { CtaWithBgColourBlockProps } from './ctaWithBgColourBlock.state'

export const ctaWithBgColour: ModernBlock<CtaWithBgColourBlockProps, any, any, DP.CTA | DP.CTAImage> = {
  kind: 'ctaWithBgColourBlock',
  render: (setRootState, rootState, blockIndex, blockProps, amountNested) =>
    fromJSX((updateBlock) => (
      <CtaWithBgColourBlockLayout.Inner amountNested={amountNested!} hasImage={blockProps.image != undefined} design={blockProps.design} displayOption={blockProps.displayOption}>
        <CtaWithBgColourBlockLayout.Content
          hasImage={blockProps.image != undefined}
          label={blockProps.label}
          title={blockProps.title}
          intro={blockProps.intro}
          showPrimary={blockProps.showPrimary}
          primaryAction={blockProps.primaryAction!}
          showSecondary={blockProps.showSecondary}
          secondaryAction={blockProps.secondaryAction!}
          showActionAsLink={blockProps.showActionAsLink}
          design={blockProps.design}
        />
        <CtaWithBgColourBlockLayout.Visual image={blockProps.image} design={blockProps.design} />
      </CtaWithBgColourBlockLayout.Inner>
    )),
  blockClassName: (props) => `
    ${getClassNameModern(props.design === 'new' ? ctaBlockClassesNew : ctaBlockClassesDefault, 'block', 'mb-6 py-6')}
    ${props.showBgColour ? CtaWithBgColourBlockLayout.ColorSchemeClassName(props.bgColour) : 'bg-light'}
  `,
  inView: {
    showAs: 'section',
    threshold: 0.1,
    triggerOnce: true,
    inViewClassName: getClassNameModern(ctaBlockClassesDefault, 'inview', 'inview'),
    updateState: (currentInviewState, currentBlockState) => {
      return {
        ...currentBlockState,
        inviewState: currentInviewState,
      }
    },
  },
  adapters: {
    drupal: (dpBlock: DP.CTA | DP.CTAImage | DP.CentralCta, blockIndex: number) => {
      const targetId = dpBlock.target.type[0].target_id

      if (targetId != 'cta' && targetId != 'cta_with_image' && targetId != 'central_call_to_action') return undefined

      const morphDpBlock = (block: DP.CTA | DP.CTAImage | DP.CentralCta): DP.CTA | DP.CTAImage => {
        if (targetId == 'central_call_to_action') {
          dpBlock = dpBlock as DP.CentralCta
          return dpBlock.target.field_block[0] as any as DP.CTA
        }
        return block as DP.CTA | DP.CTAImage
      }

      dpBlock = morphDpBlock(dpBlock)

      const fieldMediaImage = dpBlock.target.field_image[0]?.target.field_media_image[0]
      const fieldMediaSource = dpBlock.target.field_image[0]?.target.field_source[0]
        ? dpBlock.target.field_image[0]?.target.field_source
        : undefined
      const dpImages = fieldMediaImage
        ? parseDrupalImageUrls(fieldMediaImage, fieldMediaSource, ctaSrcSet, ctaSrc)?.image
        : undefined
      return {
        title: dpBlock.target.field_title ? dpBlock.target.field_title[0]?.value : undefined,
        excludeFromPageMenu: dpBlock.target?.field_exclude_from_page_menu?.[0]?.value,
        intro: dpBlock.target.field_introduction ? dpBlock.target.field_introduction[0]?.value : undefined,
        label: dpBlock.target.field_label ? dpBlock.target.field_label[0]?.value : undefined,
        design: dpBlock.target.field_design && dpBlock.target.field_design[0]?.value == 'new' ? 'new' : 'default',
        display: dpBlock.target.field_design && dpBlock.target.field_design[0]?.value == 'new' ? 'new' : 'default',
        displayOption: dpBlock.target.field_display_option[0]?.value == 'content-width' ? 'content-width' : 'full-width',
        showBgColour: true,
        bgColour: 'red', // OVERWRITE INSTEAD OF `bgColour: dpBlock.target.field_background_color ? (dpBlock.target.field_background_color[0]?.value == 'blue' ? 'blue' : 'red') : 'blue',`
        showPrimary: dpBlock.target?.field_links[0] ? true : false,
        showSecondary: dpBlock.target?.field_links[1] ? true : false,
        primaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[0]?.options?.attributes?.id! : '',
        }),
        secondaryAction: linkWrapper({
          title: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.title! : '',
          url: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.processed_url! : '',
          anchor: dpBlock.target?.field_links ? dpBlock.target?.field_links[1]?.options?.attributes?.id! : '',
        }),
        showActionAsLink: dpBlock.target.field_link_type[0]?.value == true ? true : false,
        anchorTitle: dpBlock.target.field_title[0]
          ? slugify(dpBlock.target.field_title[0].value)
          : `${targetId}-title-${blockIndex}`,
        image: dpImages,
      }
    },
  },
  seoEvents: (props, _) => [
    {
      event: 'onClick',
      category: 'cta',
      action: `primary-${slugify(props.title || '')}`,
      label: window.location.href,
      id: 'ctaPrimaryAction',
    } as SeoEvent,
    {
      event: 'onClick',
      category: 'cta',
      action: `secondary-${slugify(props.title || '')}`,
      label: window.location.href,
      id: 'ctaSecondaryAction',
    } as SeoEvent,
  ],
  sampleExample: [
    {
      title: randomTitle(),
      label: randomWords(),
      anchorTitle: 'cta-sample-1',
      intro: dummyData.intro,
      design: 'default',
      showPrimary: true,
      primaryAction: dummyData.primaryButton,
      showSecondary: true,
      secondaryAction: dummyData.secondaryButton,
      showActionAsLink: false,
      showBgColour: true,
      bgColour: 'red',
      displayOption: 'full-width',
    },
    {
      title: randomTitle(),
      anchorTitle: 'cta-sample-2',
      intro: dummyData.intro,
      design: 'default',
      showPrimary: true,
      primaryAction: dummyData.primaryButton,
      showSecondary: false,
      showActionAsLink: true,
      showBgColour: true,
      bgColour: 'red',
      image: dummyData.image,
      displayOption: 'full-width',
    },
    {
      title: randomTitle(),
      anchorTitle: 'cta-sample-3',
      intro: dummyData.intro,
      design: 'new',
      showPrimary: true,
      primaryAction: dummyData.primaryButton,
      showSecondary: false,
      showActionAsLink: true,
      showBgColour: true,
      bgColour: 'red',
      displayOption: 'full-width',
    },
    {
      title: randomTitle(),
      anchorTitle: 'cta-sample-4',
      intro: dummyData.intro,
      design: 'new',
      showPrimary: true,
      primaryAction: dummyData.primaryButton,
      showSecondary: false,
      showActionAsLink: true,
      showBgColour: true,
      bgColour: 'red',
      image: dummyData.image,
      displayOption: 'full-width',
    },
  ],
}
